import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import type {
  AuthBootstrapData,
  EffectiveRole,
  MeResponse,
  PermissionGrant,
  TenantInfo,
  TokenPairResponse,
} from '@/core/types/auth';
import { getEffectiveRole } from './permissions';

export type AuthStatus = 'idle' | 'bootstrapping' | 'authenticated' | 'anonymous';

interface AuthState {
  status: AuthStatus;
  accessToken: string | null;
  refreshToken: string | null;
  expiresAtUtc: string | null;
  me: MeResponse | null;
  tenant: TenantInfo | null;
  permissions: PermissionGrant[];
  role: EffectiveRole;
  setStatus: (status: AuthStatus) => void;
  setTokens: (tokens: TokenPairResponse) => void;
  setBootstrapData: (data: AuthBootstrapData) => void;
  clearSession: () => void;
}

const anonymousState = {
  accessToken: null,
  refreshToken: null,
  expiresAtUtc: null,
  me: null,
  tenant: null,
  permissions: [] as PermissionGrant[],
  role: 'employee' as EffectiveRole,
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      status: 'idle',
      ...anonymousState,
      setStatus: (status) => set({ status }),
      setTokens: (tokens) =>
        set({
          accessToken: tokens.accessToken,
          refreshToken: tokens.refreshToken,
          expiresAtUtc: tokens.expiresAtUtc,
        }),
      setBootstrapData: (data) =>
        set({
          status: 'authenticated',
          me: data.me,
          tenant: data.tenant,
          permissions: data.permissions,
          role: getEffectiveRole(data.permissions),
        }),
      clearSession: () => set({ status: 'anonymous', ...anonymousState }),
    }),
    {
      name: 'crm-auth-session-v1',
      version: 1,
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        expiresAtUtc: state.expiresAtUtc,
        me: state.me,
        tenant: state.tenant,
        permissions: state.permissions,
        role: state.role,
      }),
      onRehydrateStorage: () => (state) => {
        state?.setStatus(state.accessToken && state.refreshToken ? 'idle' : 'anonymous');
      },
    },
  ),
);

export const authStore = useAuthStore;
